import { Link } from "@inertiajs/react"
import { X } from "lucide-react"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import ProductLink from "@/components/productQuickView/ProductLink"
import type { Market } from "@/lib/useMarket"
import { DAYS_THAT_CAN_CHANGE } from "@/lib/sales"
import { formatUnits, formatValue, formatValueFull } from "../format"
import { NAME_SORT, TOTAL_SORT, sortRows } from "../sort"
import { type Cell, type Pivot, type PivotRow, type RowKind, type SortDir, seriesColor } from "../types"
import SortableHead from "./SortableHead"

interface Props {
  pivot: Pivot
  rows: RowKind
  currency: string | null
  /** All-markets page: show each cell's `display` figure in this currency, native underneath. */
  displayCurrency?: string | null
  rowsHeading: string
  sort: string | null
  dir: SortDir
  onSort: (key: string) => void
  header?: React.ReactNode
  onRemoveRow?: (row: PivotRow) => void
  /** Where clicking a row name goes (drill-down); null keeps the name plain. */
  hrefForRow?: (row: PivotRow) => string | null
  /** Product rows open the quick view on this market; leave it out on a market page. */
  productMarket?: Market | null
}

const CELL = "whitespace-nowrap px-4 py-2.5 text-right tabular-nums"
// Alignment is added per cell (text-left / text-right), never here — two
// text-align utilities on one element resolve by stylesheet order, not class order.
const HEAD = "h-11 whitespace-nowrap px-4 align-middle text-[13px] font-medium text-muted-foreground"
// Sticky cells need a solid background so scrolled columns don't show through;
// the total tint is mixed to a solid color for the same reason.
const STICKY = "sticky left-0 z-10 border-r bg-card"
const TOTAL_BG = { background: "color-mix(in srgb, var(--muted) 50%, var(--card))" }

interface ValueCellProps {
  cell: Cell
  showUnits: boolean
  currency: string | null
  /** When set and different from `currency`, the converted figure leads and the native one sits underneath. */
  displayCurrency?: string | null
  className: string
  style?: React.CSSProperties
}

const SUB = "block text-[11px] font-normal text-muted-foreground"

function ValueCell({ cell, showUnits, currency, displayCurrency, className, style }: ValueCellProps) {
  const converted = displayCurrency != null && displayCurrency !== currency
  if (!converted) {
    return (
      <td className={className} style={style} title={formatValueFull(cell.revenue, currency)}>
        {formatValue(cell.revenue, currency)}
        {showUnits && <span className={SUB}>{formatUnits(cell.quantity)}</span>}
      </td>
    )
  }
  const missing = cell.display == null
  return (
    <td className={className} style={style} title={missing ? "No exchange rate yet" : formatValueFull(cell.display!, displayCurrency)}>
      {missing ? <span className="text-muted-foreground">–</span> : formatValue(cell.display!, displayCurrency)}
      <span className={SUB}>{formatValue(cell.revenue, currency)}</span>
      {showUnits && <span className={SUB}>{formatUnits(cell.quantity)}</span>}
    </td>
  )
}

export default function RevenuePivotTable({ pivot, rows, currency, displayCurrency, rowsHeading, sort, dir, onSort, header, onRemoveRow, hrefForRow, productMarket = null }: Props) {
  const money = { currency, displayCurrency }
  const showSwatch = rows === "channels" && pivot.rows.length > 1
  // Units only make sense for one product; channel rows and the Total row mix
  // products, so they stay revenue-only.
  const showUnits = rows === "products"
  // Sorting moves whole rows. The color dot stays with the channel (chart
  // order), so it comes from the row's unsorted position.
  const ordered = sortRows(pivot.rows.map((row, colorIndex) => ({ row, colorIndex })), { sort, dir })
  const activeDir = (key: string): SortDir | null => (sort === key ? dir : null)
  const hrefOf = (row: PivotRow) => hrefForRow?.(row) ?? null
  const drillable = pivot.rows.some((row) => hrefOf(row) !== null)

  return (
    <Card className="overflow-hidden p-0">
      {header}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-b">
              <SortableHead sortKey={NAME_SORT} active={activeDir(NAME_SORT)} onSort={onSort} align="left" className={`${STICKY} ${HEAD} text-left`}>
                {rowsHeading}
              </SortableHead>
              {pivot.columns.map((c) => (
                <SortableHead
                  key={c.key}
                  sortKey={c.key}
                  active={activeDir(c.key)}
                  onSort={onSort}
                  align="right"
                  className={`${HEAD} text-right`}
                  note={c.partial && <span className="text-[10px] font-normal">partial</span>}
                >
                  {c.label}
                </SortableHead>
              ))}
              <SortableHead sortKey={TOTAL_SORT} active={activeDir(TOTAL_SORT)} onSort={onSort} align="right" className={`${HEAD} text-right`} style={TOTAL_BG}>
                Total
              </SortableHead>
            </tr>
          </thead>
          <tbody>
            {pivot.rows.length === 0 && (
              <tr>
                <td colSpan={pivot.columns.length + 2} className="px-4 py-8 text-center text-muted-foreground">
                  No sales in this period.
                </td>
              </tr>
            )}
            {ordered.map(({ row, colorIndex }) => {
              const href = hrefOf(row)
              return (
              <tr key={row.key} className="border-b">
                <td className={`${STICKY} px-4 py-2`}>
                  <div className="flex items-center gap-2.5">
                    {showSwatch && (
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: seriesColor(colorIndex) }} />
                    )}
                    <div className="flex min-w-0 max-w-[320px] flex-col">
                      {showUnits ? (
                        // Product rows: the key is the product's external id.
                        <ProductLink externalId={row.key} market={productMarket} title={row.label} className="font-medium">
                          <span className="truncate">{row.label}</span>
                        </ProductLink>
                      ) : href ? (
                        <Link href={href} className="truncate font-medium text-link hover:underline" title={row.label}>
                          {row.label}
                        </Link>
                      ) : (
                        <span className="truncate font-medium" title={row.label}>{row.label}</span>
                      )}
                      {row.sublabel && <span className="text-[11px] text-muted-foreground">{row.sublabel}</span>}
                    </div>
                    {onRemoveRow && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-auto h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={() => onRemoveRow(row)}
                        aria-label={`Remove ${row.label}`}
                        title="Remove from list"
                      >
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                </td>
                {pivot.columns.map((c) => (
                  <ValueCell key={c.key} cell={row.cells[c.key]} showUnits={showUnits} {...money} className={CELL} />
                ))}
                <ValueCell cell={row.total} showUnits={showUnits} {...money} className={`${CELL} font-medium`} style={TOTAL_BG} />
              </tr>
              )
            })}
          </tbody>
          {pivot.rows.length > 0 && (
            <tfoot>
              <tr className="font-medium" style={TOTAL_BG}>
                <td className={`${STICKY} px-4 py-2.5`} style={TOTAL_BG}>Total</td>
                {pivot.columns.map((c) => (
                  <ValueCell key={c.key} cell={pivot.totals[c.key]} showUnits={false} {...money} className={CELL} />
                ))}
                <ValueCell cell={pivot.grand_total} showUnits={false} {...money} className={`${CELL} font-semibold`} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="border-t px-4 py-2.5 text-xs text-muted-foreground">
        {drillable && "Click a channel name to see what is under it. "}
        Click a column heading to sort. Sales sync nightly. The last {DAYS_THAT_CAN_CHANGE} days can still change. Gift orders are excluded.
        {pivot.columns.some((c) => c.partial) && " Columns marked partial cover only part of the period."}
      </div>
    </Card>
  )
}
